
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, ArrowUpRight, ArrowDownRight, RefreshCw, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { Contract } from "@/lib/types";
import { ExpirySticker } from "./ExpirySticker";
import { TypeTooltip } from "./TypeTooltip";
import { CategoryTooltip } from "./CategoryTooltip";
import { getExpiryStatusText, importanceBadges } from "./types";
import { useImportanceColors } from "@/hooks/useImportanceColors";
import { getDirectionIcon } from "@/components/contract-types/ContractDirectionIcons";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { FeatureGuard } from "@/components/common/FeatureGuard";

interface ContractWithDetails extends Contract {
  importanceColor?: string;
  convertedAmount?: number | null;
  conversionRate?: number;
  isConverting?: boolean;
  isGroupHeader?: boolean;
  convertedCurrency?: {
    code: string;
    symbol: string;
  } | null;
}

interface ContractListViewProps {
  contracts: ContractWithDetails[];
  onDeleteContract: (id: string) => void;
  isColumnSelected: (id: string) => boolean;
  selectedColumns: string[];
  defaultCurrencySymbol?: string;
  canUseGroupedContracts?: boolean;
}

export function ContractListView({
  contracts,
  onDeleteContract,
  isColumnSelected,
  selectedColumns,
  defaultCurrencySymbol,
  canUseGroupedContracts = false
}: ContractListViewProps) {
  const { getImportanceColor } = useImportanceColors();
  const { canUseCurrencyConversion, canUsePriceComparison } = useFeatureAccess();

  // Filter out group headers if user doesn't have grouped contracts feature
  const filteredContracts = canUseGroupedContracts
    ? contracts
    : contracts.filter(contract => !contract.isGroupHeader);

  return (
    <div className="space-y-3">
      {filteredContracts.map((contract) => {
        const isIncome = contract.type?.direction === "incoming";
        
        return (
          <div
            key={contract.id}
            className="p-4 rounded-md border dark:border-gray-800 bg-card hover:bg-accent/10 dark:hover:bg-accent/5 transition-colors"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{contract.supplierName}</h3>
                  
                  {isColumnSelected("expiryStatus") && (
                    <ExpirySticker contract={contract} />
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {isColumnSelected("importance") && (
                    (() => {
                      const { className, style } = getImportanceColor(contract.importance);
                      return (
                        <Badge 
                          variant={importanceBadges[contract.importance]?.variant || "outline"}
                          className={className}
                          style={style}
                        >
                          {importanceBadges[contract.importance]?.label || `Importance ${contract.importance}`}
                        </Badge>
                      );
                    })()
                  )}

                  {isColumnSelected("type") && contract.type && (
                    <TypeTooltip type={contract.type}>
                      <Badge variant="outline" className="border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-300 flex items-center gap-1.5">
                        {contract.type.direction && getDirectionIcon(contract.type.direction, "h-3.5 w-3.5 mr-1")}
                        {contract.type.name}
                      </Badge>
                    </TypeTooltip>
                  )}

                  {isColumnSelected("category") && contract.category && (
                    <CategoryTooltip category={contract.category}>
                      <Badge variant="outline" className="border-green-200 text-green-700 dark:border-green-800 dark:text-green-300">
                        {contract.category.name}
                      </Badge>
                    </CategoryTooltip>
                  )}
                  
                  {isColumnSelected("currency") && contract.currency && (
                    <Badge variant="outline" className="text-xs">
                      {contract.currency.code}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 items-center">
                <div className="space-y-1">
                  {isColumnSelected("signedDate") && contract.signedDate && (
                    <div className="text-sm">
                      <span className="text-muted-foreground mr-2">Signed:</span>
                      <span className="font-medium">{format(new Date(contract.signedDate), "PP")}</span>
                    </div>
                  )}
                  
                  {isColumnSelected("expiryDate") && contract.expiryDate && (
                    <div className="text-sm">
                      <span className="text-muted-foreground mr-2">Expires:</span>
                      <span className="font-medium">{format(new Date(contract.expiryDate), "PP")}</span>
                    </div>
                  )}
                </div>
                
                {isColumnSelected("expectedCost") && contract.expectedCost && (
                  <div className="text-sm flex flex-col">
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground mr-2">Value:</span>
                      <div className="flex items-center gap-1">
                        {isIncome ? (
                          <ArrowUpRight className="h-4 w-4 text-green-500" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4 text-red-500" />
                        )}
                        <span className={`font-medium ${isIncome ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                          {contract.currency?.symbol}{" "}
                          {Number(contract.expectedCost).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                    
                    {contract.convertedAmount !== null && contract.convertedCurrency && (
                      <FeatureGuard feature="currency_conversion">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 text-xs ml-auto mt-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900 rounded-full cursor-help max-w-fit">
                                <RefreshCw className="h-3 w-3 text-blue-500" />
                                <span className="text-blue-700 dark:text-blue-300">
                                  {contract.convertedAmount.toLocaleString(undefined, {maximumFractionDigits: 2})} 
                                  {contract.convertedCurrency.symbol}
                                </span>
                                <AlertTriangle className="h-3 w-3 text-amber-500 ml-0.5 opacity-70" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="bg-black text-white border-gray-800 p-3 max-w-[300px]">
                              <div className="space-y-2">
                                <p>Converted at rate: 1 {contract.currency?.code} = {contract.conversionRate?.toFixed(4)} {contract.convertedCurrency.code}</p>
                                <p className="text-amber-300 text-xs flex items-start gap-1.5">
                                  <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                  <span>Currency conversions are estimates and may not reflect exact rates at the time of contract payment.</span>
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </FeatureGuard>
                    )}
                    
                    {contract.isConverting && canUseCurrencyConversion && (
                      <span className="ml-auto text-xs text-gray-400">converting...</span>
                    )}
                  </div>
                )}
                
                {isColumnSelected("actions") && (
                  <div className="ml-auto">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => onDeleteContract(contract.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
